import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, ArrowLeft, Heart } from "lucide-react";
import slides from "../data/mutterstimmeSlides";

/* MUTTERSTIMME palette — warm cream + rosé + roségold (distinct from VATERSTIMME's dark gold theme) */
const M = {
  cream: "#F7EEDD",          // page background
  creamSoft: "#FAF3E7",      // card / inner background
  rose: "#C77B7B",           // primary accent
  roseDeep: "#A65A5A",       // sidebar / strong text
  roseGold: "#D4877A",       // secondary accent
  gold: "#D4AF37",           // SAT gold reserved for special accents
  ink: "#3A2828",            // primary dark text
  inkSoft: "#5B4040",        // secondary text
  whisper: "#7A5050",        // tertiary text
};

/* Compass-Rose Brand-Mark in roségold */
const CompassMark = ({ size = 32, color = M.gold }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden>
    <circle cx="32" cy="32" r="29" fill="none" stroke={color} strokeWidth="1.2" opacity="0.55" />
    <g fill={color}>
      <path d="M32 6 L34.2 30 L32 32 L29.8 30 Z" />
      <path d="M32 58 L34.2 34 L32 32 L29.8 34 Z" opacity="0.7" />
      <path d="M6 32 L30 29.8 L32 32 L30 34.2 Z" opacity="0.7" />
      <path d="M58 32 L34 29.8 L32 32 L34 34.2 Z" opacity="0.7" />
    </g>
    <circle cx="32" cy="32" r="2" fill={color} />
  </svg>
);

/* ── COVER ─────────────────────────────────────────────────────────────── */
const Cover = ({ s }) => (
  <div
    className="w-full flex flex-col items-center justify-center text-center px-8 lg:px-16 py-14 relative min-h-[620px]"
    style={{ color: M.ink }}
  >
    <div className="absolute top-8 left-1/2 -translate-x-1/2 opacity-90">
      <CompassMark size={64} color={M.gold} />
    </div>
    <div className="mt-16">
      <h1
        className="serif font-light tracking-tight leading-[0.95]"
        style={{ fontSize: "clamp(2.5rem,7vw,6.5rem)" }}
      >
        <span style={{ color: M.rose }}>MUTTER</span>
        <span style={{ color: M.roseGold }}>STIMME</span>
        <sup className="text-xl md:text-2xl align-super ml-1" style={{ color: M.gold }}>™</sup>
      </h1>
      <div
        className="mx-auto mt-4 w-32 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${M.gold}, transparent)` }}
      />
    </div>

    <p className="serif italic mt-7 text-xl md:text-2xl max-w-3xl leading-snug" style={{ color: M.rose }}>
      {s.subtitle}
    </p>
    {s.intro && (
      <p className="max-w-3xl mt-5 text-sm md:text-base leading-relaxed" style={{ color: M.inkSoft }}>
        {s.intro}
      </p>
    )}

    {/* 5 Pillars */}
    {s.pillars && (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-10 max-w-5xl w-full">
        {s.pillars.map((p, i) => (
          <div
            key={i}
            className="rounded-2xl p-4 border"
            style={{
              borderColor: `${M.rose}33`,
              background: "rgba(199,123,123,0.06)",
            }}
          >
            <div
              className="text-[10px] tracking-[0.22em] font-bold mb-2"
              style={{ color: M.rose }}
            >
              {p.h}
            </div>
            <div className="text-xs leading-relaxed" style={{ color: M.inkSoft }}>
              {p.t}
            </div>
          </div>
        ))}
      </div>
    )}

    {s.tagline && (
      <div
        className="mt-10 text-[11px] md:text-xs tracking-[0.4em] font-semibold uppercase"
        style={{ color: M.roseDeep }}
      >
        {s.tagline}
      </div>
    )}
    {s.closing && (
      <div className="serif italic mt-3 text-lg md:text-xl" style={{ color: M.roseGold }}>
        {s.closing}
      </div>
    )}

    {/* Side card on cover (MUTTERSTIMME steht für) */}
    {s.side && (
      <div
        className="mt-10 rounded-2xl border p-6 max-w-4xl w-full text-left"
        style={{
          borderColor: `${M.rose}33`,
          background: "rgba(199,123,123,0.07)",
        }}
      >
        <div
          className="text-[11px] tracking-[0.32em] font-bold mb-3 text-center"
          style={{ color: M.rose }}
        >
          {s.side.title}
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {s.side.items.map((it, i) => (
            <div key={i} className="flex items-start gap-2 text-sm" style={{ color: M.inkSoft }}>
              <Heart size={12} className="shrink-0 mt-1" style={{ color: M.rose, fill: M.rose }} />
              <span>{it}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

/* ── CONTENT ───────────────────────────────────────────────────────────── */
const Content = ({ s }) => {
  const isGrid = s.layout === "grid";
  const dense = isGrid;

  return (
    <div className="w-full px-8 lg:px-14 py-10 lg:py-12" style={{ color: M.ink }}>
      {/* Header */}
      <div className="mb-6 lg:mb-8 max-w-5xl">
        {s.kicker && (
          <div
            className="text-[10px] tracking-[0.35em] uppercase font-bold mb-2"
            style={{ color: M.roseDeep }}
          >
            {s.kicker}
          </div>
        )}
        <h2
          className="serif font-light leading-tight"
          style={{
            color: M.rose,
            fontSize: dense ? "clamp(1.75rem,2.6vw,2.4rem)" : "clamp(2rem,3.2vw,3rem)",
          }}
        >
          {s.title}
        </h2>
        {s.subtitle && (
          <p
            className="text-base lg:text-lg mt-3 max-w-3xl leading-relaxed"
            style={{ color: M.inkSoft }}
          >
            {s.subtitle}
          </p>
        )}
        {s.intro && (
          <p
            className="text-sm lg:text-base mt-3 max-w-3xl leading-relaxed serif italic"
            style={{ color: M.roseDeep }}
          >
            {s.intro}
          </p>
        )}
      </div>

      <div className={`grid ${dense ? "lg:grid-cols-[1.55fr_1fr]" : "lg:grid-cols-[1fr_1fr]"} gap-8 lg:gap-10`}>
        {/* LEFT — bullets */}
        <div>
          {isGrid ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {s.bullets?.map((b, i) => (
                <div
                  key={i}
                  className="rounded-2xl border p-4"
                  style={{
                    borderColor: `${M.rose}33`,
                    background: "rgba(199,123,123,0.05)",
                  }}
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
                    style={{ background: M.rose }}
                  >
                    <Heart size={16} style={{ color: M.cream, fill: M.cream }} />
                  </div>
                  <div
                    className="font-semibold tracking-wide uppercase text-[11px] mb-1"
                    style={{ color: M.rose }}
                  >
                    {b.h}
                  </div>
                  <div className="text-xs leading-relaxed" style={{ color: M.inkSoft }}>
                    {b.t}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ul className="space-y-4">
              {s.bullets?.map((b, i) => (
                <li key={i} className="flex gap-4">
                  <div
                    className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5"
                    style={{ background: M.rose, color: M.cream }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <div
                      className="font-semibold tracking-wide uppercase text-sm"
                      style={{ color: M.rose }}
                    >
                      {b.h}
                    </div>
                    {b.t && (
                      <div className="text-sm md:text-[15px] leading-relaxed mt-1" style={{ color: M.inkSoft }}>
                        {b.t}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* CTA banner under bullets */}
          {s.cta && (
            <div
              className="mt-6 rounded-2xl p-5 text-center border"
              style={{
                borderColor: `${M.rose}55`,
                background: "rgba(199,123,123,0.08)",
              }}
            >
              <div
                className="serif text-lg lg:text-xl font-light mb-1"
                style={{ color: M.rose }}
              >
                {s.cta.title}
              </div>
              <div className="text-sm" style={{ color: M.inkSoft }}>
                {s.cta.sub}
              </div>
            </div>
          )}

          {/* Secondary numbered-steps strip (e.g., MUTTERSTIMME Bewegung) */}
          {s.secondary && (
            <div
              className="mt-6 rounded-2xl border p-5"
              style={{ borderColor: `${M.rose}33`, background: "rgba(199,123,123,0.04)" }}
            >
              <div
                className="text-[11px] uppercase tracking-[0.32em] font-bold mb-4 text-center"
                style={{ color: M.rose }}
              >
                {s.secondary.title}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {s.secondary.steps?.map((st, i) => (
                  <div key={i} className="text-center">
                    <div
                      className="mx-auto w-9 h-9 rounded-full flex items-center justify-center mb-2"
                      style={{ background: M.rose }}
                    >
                      <Heart size={14} style={{ color: M.cream, fill: M.cream }} />
                    </div>
                    <div
                      className="font-bold uppercase text-[10px] tracking-wider mb-1"
                      style={{ color: M.roseDeep }}
                    >
                      {st.h}
                    </div>
                    <div className="text-[10px] leading-snug" style={{ color: M.inkSoft }}>
                      {st.t}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — sidebar */}
        <div className="flex flex-col gap-4">
          {s.mantra && (
            <div
              className="relative rounded-2xl p-5 border"
              style={{
                borderColor: `${M.rose}55`,
                background: "rgba(199,123,123,0.06)",
              }}
            >
              <div className="text-2xl absolute top-1 left-4 serif" style={{ color: M.rose }}>
                &ldquo;
              </div>
              <p className="serif italic text-base lg:text-lg leading-snug pt-2" style={{ color: M.roseDeep }}>
                {s.mantra}
              </p>
              {s.mantraSub && (
                <p className="text-[10px] mt-3 tracking-[0.25em] uppercase font-semibold" style={{ color: M.rose }}>
                  {s.mantraSub}
                </p>
              )}
            </div>
          )}

          {s.side && (
            <div
              className="rounded-2xl border p-5"
              style={{ borderColor: `${M.rose}33`, background: "rgba(199,123,123,0.04)" }}
            >
              <div
                className="text-[11px] uppercase tracking-[0.32em] font-bold mb-3"
                style={{ color: M.rose }}
              >
                {s.side.title}
              </div>
              <div className="space-y-1.5">
                {s.side.items.map((it, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs leading-relaxed" style={{ color: M.inkSoft }}>
                    <Heart size={10} className="shrink-0 mt-1" style={{ color: M.gold, fill: M.gold }} />
                    <span>{it}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {s.badge && (
            <div
              className="rounded-2xl border p-5"
              style={{ borderColor: `${M.gold}55`, background: "rgba(212,175,55,0.06)" }}
            >
              <div className="flex items-center gap-2 mb-2">
                <CompassMark size={20} color={M.gold} />
                <div className="text-[10px] tracking-[0.32em] font-bold" style={{ color: M.roseDeep }}>
                  {s.badge.title}
                </div>
              </div>
              {s.badge.lines?.map((l, i) => (
                <div key={i} className="text-[11px] leading-snug" style={{ color: M.inkSoft }}>
                  {l}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom-wide footerStrip (e.g. 6 Werte) */}
      {s.footerStrip && (
        <div
          className="mt-6 rounded-2xl border p-5"
          style={{ borderColor: `${M.rose}33`, background: "rgba(199,123,123,0.05)" }}
        >
          {s.footerStripTitle && (
            <div
              className="text-[11px] uppercase tracking-[0.32em] font-bold mb-4 text-center"
              style={{ color: M.rose }}
            >
              {s.footerStripTitle}
            </div>
          )}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {s.footerStrip.map((f, i) => (
              <div key={i} className="text-center">
                <div
                  className="w-9 h-9 rounded-full mx-auto mb-2 flex items-center justify-center"
                  style={{ background: M.rose }}
                >
                  <Heart size={14} style={{ color: M.cream, fill: M.cream }} />
                </div>
                <div className="font-bold uppercase text-[10px] tracking-wider" style={{ color: M.roseDeep }}>
                  {f.h}
                </div>
              </div>
            ))}
          </div>
          {s.footerStripClosing && (
            <div
              className="mt-4 text-center serif italic text-sm"
              style={{ color: M.roseDeep }}
            >
              {s.footerStripClosing}
            </div>
          )}
        </div>
      )}

      {/* Closing line (final slide) */}
      {s.closing && (
        <div className="mt-6 text-center">
          <div className="serif text-xl lg:text-2xl font-light" style={{ color: M.rose }}>
            {s.closing}
          </div>
        </div>
      )}
    </div>
  );
};

const render = (s) => (s.type === "cover" ? <Cover s={s} /> : <Content s={s} />);

export default function SatMutterstimmeDeck() {
  const [idx, setIdx] = useState(0);
  const [fs, setFs] = useState(false);
  const total = slides.length;
  const go = useCallback((d) => setIdx((i) => Math.min(total - 1, Math.max(0, i + d))), [total]);

  useEffect(() => {
    const k = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "f") setFs((f) => !f);
      else if (e.key === "Escape") setFs(false);
      else if (e.key === "Home") setIdx(0);
      else if (e.key === "End") setIdx(total - 1);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go, total]);

  const slide = slides[idx];

  return (
    <div
      className={`${fs ? "fixed inset-0 z-[100]" : "min-h-screen pt-24 pb-12"}`}
      style={{ background: "#0E1A30", color: M.ink }}
      data-testid="sat-mutterstimme-deck"
    >
      <div className={`${fs ? "h-screen" : ""} container-sat`}>
        {!fs && (
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/akademie/decks"
              className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase transition hover:translate-x-[-2px]"
              style={{ color: M.roseGold }}
              data-testid="mutterstimme-deck-back"
            >
              <ArrowLeft size={12} /> Zurück
            </Link>
            <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: "rgba(255,255,255,0.5)" }}>
              MUTTERSTIMME · Initiative der SAT Akademie
            </div>
            <button
              onClick={() => setFs(true)}
              className="flex items-center gap-2 text-xs uppercase tracking-wider transition"
              style={{ color: M.roseGold }}
              data-testid="mutterstimme-deck-fullscreen-on"
            >
              <Maximize2 size={14} /> Vollbild
            </button>
          </div>
        )}

        <div
          className={`relative ${fs ? "h-full" : "min-h-[680px]"} rounded-2xl overflow-hidden`}
          style={{
            background: `linear-gradient(135deg, ${M.cream} 0%, ${M.creamSoft} 60%, ${M.cream} 100%)`,
            border: fs ? "none" : `1px solid ${M.rose}33`,
          }}
          data-testid={`mutterstimme-slide-${slide.num}`}
        >
          {/* LEFT brand strip */}
          <div
            className="absolute top-0 bottom-0 left-0 w-[80px] lg:w-[120px] flex flex-col items-center py-10 z-10"
            style={{ background: M.roseDeep, color: M.cream }}
          >
            <CompassMark size={44} color={M.gold} />
            <div className="mt-5 text-center leading-tight">
              <div className="serif text-lg font-light tracking-wider">SAT</div>
              <div className="text-[7px] tracking-[0.25em] uppercase mt-1 px-2 opacity-85">
                Schweizer Akademie
              </div>
              <div className="text-[7px] tracking-[0.25em] uppercase opacity-85">
                für Transformation
              </div>
            </div>
            <div className="mt-6 text-base" style={{ color: M.gold }}>♥</div>
            <div
              className="hidden lg:block mt-6 text-center px-2"
              style={{ fontFamily: "Georgia, serif", color: M.cream, fontStyle: "italic" }}
            >
              <div className="text-sm">Nenad</div>
              <div className="text-sm">Vladović</div>
              <div className="text-[8px] tracking-[0.18em] uppercase mt-2 opacity-80">
                Gründer der SAT Akademie
              </div>
              <div className="text-[8px] tracking-[0.18em] uppercase opacity-80">
                für Transformation
              </div>
            </div>
            <div className="mt-auto pb-2 text-center text-[8px] tracking-[0.25em] uppercase opacity-85 leading-relaxed hidden lg:block">
              <div>Deine Stimme.</div>
              <div>Deine Selbstfürsorge.</div>
              <div>Deine Kraft.</div>
              <div>Deine Liebe.</div>
              <div>Dein Erbe.</div>
            </div>
          </div>

          {/* Page counter top-right */}
          <div
            className="absolute top-5 right-6 text-xs tracking-[0.3em] uppercase font-light z-10"
            style={{ color: M.roseDeep }}
          >
            {String(slide.num).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>

          {/* Slide body — offset for left strip */}
          <div className="pl-[80px] lg:pl-[120px]">
            <div className="pt-6 pb-12">{render(slide)}</div>
          </div>

          {/* Footer */}
          <div
            className="absolute bottom-3 left-[80px] lg:left-[120px] right-0 text-center text-[9px] tracking-[0.3em] uppercase"
            style={{ color: `${M.roseDeep}99` }}
          >
            MUTTERSTIMME™ — Eine Initiative der SAT Akademie für Transformation
          </div>

          {fs && (
            <button
              onClick={() => setFs(false)}
              className="absolute top-5 right-1/2 translate-x-1/2 flex items-center gap-2 text-xs uppercase tracking-wider z-20"
              style={{ color: M.roseDeep }}
              data-testid="mutterstimme-deck-fullscreen-off"
            >
              <Minimize2 size={14} /> Schließen (Esc)
            </button>
          )}
        </div>

        {/* Controls */}
        <div className="mt-5 flex items-center justify-between">
          <button
            onClick={() => go(-1)}
            disabled={idx === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm transition disabled:opacity-30"
            style={{ borderColor: `${M.roseGold}55`, color: M.roseGold }}
            data-testid="mutterstimme-deck-prev"
          >
            <ChevronLeft size={16} /> Vorherige
          </button>
          <div className="hidden md:flex items-center gap-1.5 max-w-2xl overflow-x-auto">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className="shrink-0 transition-all rounded-full"
                style={{
                  width: i === idx ? "24px" : "8px",
                  height: "8px",
                  background: i === idx ? M.rose : "rgba(199,123,123,0.3)",
                }}
                aria-label={`Slide ${i + 1}`}
                data-testid={`mutterstimme-deck-dot-${i}`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            disabled={idx === total - 1}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition disabled:opacity-30"
            style={{ background: M.rose, color: M.cream }}
            data-testid="mutterstimme-deck-next"
          >
            Nächste <ChevronRight size={16} />
          </button>
        </div>

        {!fs && (
          <div className="mt-6 text-center text-[10px] tracking-[0.3em] uppercase" style={{ color: "rgba(255,255,255,0.4)" }}>
            Pfeiltasten ◄ ► · Leertaste · F für Vollbild · Esc zum Schließen
          </div>
        )}
      </div>
    </div>
  );
}
